import { PRIMARY_OUTLET, Router } from '@angular/router';
import { Route } from '@app/core/enums/route.enum';

/** Builds an absolute URL under the home tabs, e.g. `path(MyHousing, Housing)` -> `/home/my-housing/housing`. */
const path = (...segments: Route[]): string => `/${[Route.Home, ...segments].join('/')}`;

/**
 * Tabs whose root page is NOT `/home/<tab>`. Most tabs root there, but a few
 * redirect their index to a default child (`my-housing` -> `housing`, `jobs` ->
 * `search`), so their root sits one level deeper. Only those exceptions are
 * listed; every other tab falls back to `/home/<tab>`.
 *
 * This is declared explicitly rather than inferred from the route tree: whether
 * a page is a subpage or a root-level peer depends on how it is navigated to
 * (`navigate` push vs `navigateRoot`), which the route config does not encode.
 */
const TAB_ROOT_OVERRIDES: Partial<Record<Route, string>> = {
  [Route.Jobs]: path(Route.Jobs, Route.Search),
};

/**
 * Explicit back target for specific URLs, overriding the tab-root logic. Used
 * for tab roots that are entered from elsewhere and should still navigate back
 * (e.g. `my-inbox` is opened from the dashboard). Keyed by the exact URL, so the
 * same component mounted at another path is unaffected.
 */
const BACK_HREF_OVERRIDES: Record<string, string> = {
  [path(Route.MyInbox)]: path(Route.Dashboard),
};

/**
 * Derives the back target for an Ionic back button's `defaultHref` — the
 * fallback used when there is no in-app navigation stack (deep link, push
 * notification, reload). Shared by `c-page-header` and `c-condensed-header` so
 * both agree on the back target and on whether a page is a subpage.
 *
 * Returns the current tab's root page, or null when the current page is
 * itself a root (the tab root) or lives outside the tabs (where back
 * navigation relies on the Ionic stack or an explicit `defaultHref`).
 */
export function resolveBackHref(router: Router): string | null {
  const segments = router.parseUrl(router.url).root.children[PRIMARY_OUTLET]?.segments ?? [];
  const tab = segments[1]?.path as Route | undefined;
  if (segments[0]?.path !== Route.Home || !tab) {
    return null;
  }

  const current = `/${segments.map((segment) => segment.path).join('/')}`;
  if (BACK_HREF_OVERRIDES[current]) {
    return BACK_HREF_OVERRIDES[current];
  }

  const root = TAB_ROOT_OVERRIDES[tab] ?? path(tab);
  return current === root ? null : root;
}
